import { selectWallet } from '../paymentMethods';
import logger from '../../lib/log';
import { PaymentError } from '../../lib/error';

import {
  getBraintreeInstance,
  getPaymentBreakdown,
  getToken,
  selectPaymentMethod,
  updatePaymentInfo,
} from '../../lib/wallets';

import _ from 'lodash';

// https://developers.braintreepayments.com/guides/apple-pay/client-side/javascript/v3

export const PAYMENT_METHOD_ID = 'applepay';
export const PAYMENT_METHOD_NAME = 'Apple Pay';
export const ICON_FILENAME = 'payment-apple-pay-icon.svg';
export const AUTO_COMPLETE_PURCHASE = true;

const APPLE_PAY_API_VERSION = 2;
const MERCHANT_IDENTIFIER = 'merchant.com.fandango.fandango.apple-pay-prod';
const IS_ACTIVE_CARD_REQUIRED = false;

let postalCode;

const appCallbackUrls = {
  FAIL: 'fandango://?op=purchasewithapplepayended&status=fail',
  SUCCESS: 'fandango://?op=purchasewithapplepayended&status=success',
  getAuthorizeUrl: (token, paymentBreakdown) => {
    let lineItemsCount = 0;
    let lineItemsString = '';

    paymentBreakdown.forEach(({
      amount,
      isTicket,
      label,
      quantity,
    }, idx) => {
      const applePayLabel = (isTicket || (label === 'Convenience Fee')) ? `${label} x ${quantity}` : label;
      lineItemsCount = idx + 1;
      lineItemsString += encodeURI(`&linename${lineItemsCount}=${applePayLabel}&linevalue${lineItemsCount}=${amount}`);
    });

    // eslint-disable-next-line prefer-template
    return 'fandango://?'
      + 'op=purchasewithapplepay'
      + '&btclienttoken=' + encodeURIComponent(token)
      + '&timeoutsecs=180'
      + '&timeoutmsg=' + encodeURIComponent('A communication error has occurred. Please check your purchase history to confirm that your purchase was successfully completed.')
      + '&transpendingmsg=' + encodeURIComponent('Your transaction is currently being processed and cannot be cancelled at this time.')
      + '&generrmsg=' + encodeURIComponent('Sorry, there was a problem processing your order.')
      + '&linecount=' + lineItemsCount
      + lineItemsString
      + (Commerce.state.account.emailAddress ? '' : '&additionalpaymentfields=email');
  },
};
const log = logger('ApplePay');

let authorized = false;
let braintreePaymentInstance;
let fandangoToken;
let isIosApp = false;
let rejectNativeAuthorization;
let resolveNativeAuthorization;

/** Formats the breakdown returned by the api to the shape needed by Braintree */
const formatPaymentBreakdown = (breakdown) => {
  const items = _.map(
    _.filter(breakdown, (item) => item && item.label && (item.amount || item.isTotal)),
    ({
      amount,
      label,
      isTicket,
      isTotal,
      quantity,
    }) => ({
      amount: (parseFloat(amount) / 100) || 0,
      label: (isTicket || (label === 'Convenience Fee')) ? `${label} x ${quantity}` : label,
      isTotal,
    }),
  );
  const lineItems = _.filter(items, ['isTotal', false]);
  const total = _.find(items, ['isTotal', true]);

  return total ? {
    lineItems,
    total,
  } : null;
};

/**
 * getBraintreeNonce
 *
 * Tokenizes the payment method and returns the Braintree nonce needed to complete the purchase.
 *
 * @param {Array.<Object>} paymentBreakdown - Each item should have a `label` and `amount` property.
 *   Additionally, one of the items should be flagged as the total by `isTotal`
 *
 * @returns {Promise<String>} Braintree nonce - If a nonce is not able to be generated, then the
 *   promise will be rejected with a PaymentError
 */
const getBraintreeNonce = (paymentBreakdown) => new Promise((resolve, reject) => {
  if (!braintreePaymentInstance) reject(new PaymentError());
  const { emailAddress } = Commerce.state.account;

  const paymentRequest = braintreePaymentInstance.createPaymentRequest({
    requiredBillingContactFields: ['postalAddress'], // Best practice to collect and pass along the postal code with all transactions (and keep transaction fees lower)
    requiredShippingContactFields: emailAddress ? [] : ['email'],
    ...formatPaymentBreakdown(paymentBreakdown),
  });
  const session = new window.ApplePaySession(APPLE_PAY_API_VERSION, paymentRequest);

  session.onvalidatemerchant = (event) => {
    log('onvalidatemerchant');
    braintreePaymentInstance.performValidation({
      validationURL: event.validationURL,
      displayName: 'Fandango', // TODO: should this be the business property name?
    }).then((merchantSession) => {
      log('onvalidatemerchant', merchantSession);
      session.completeMerchantValidation(merchantSession);
    }).catch((err) => {
      log('onvalidatemerchant error', err);
      session.abort();
      reject(new PaymentError(err));
    });
  };

  session.oncancel = (e) => {
    log('oncancel', e);
    reject(new PaymentError('onApplePayCancel'));
  };

  session.onpaymentauthorized = function (event) {
    log('onpaymentauthorized');
    postalCode = event?.payment?.billingContact?.postalCode;

    braintreePaymentInstance.tokenize({
      token: event.payment.token,
    }).then((payload) => {
      session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
      log('onpaymentauthorized', payload);

      resolve({
        braintreeNonce: payload.nonce,
        emailAddress: emailAddress || event.payment.shippingContact.emailAddress,
      });
    }).catch((err) => {
      session.completePayment(window.ApplePaySession.STATUS_FAILURE);
      log('onpaymentauthorized error', err);
      reject(new PaymentError(err));
    });
  };

  session.begin();
});

const onAuthorizationFailure = () => {
  if (isIosApp) {
    window.open(appCallbackUrls.FAIL, '_self');
  }
};

/**
 * init
 *
 * Initializes ApplePay as a payment method and uses `ApplePaySession` to ensure
 * that it can be used as a payment method.
 *
 * @async
 * @param {Object} options
 * @param {Boolean} options.isIosApp
 *
 * @returns {Boolean} Flag to indicate that ApplePay has been initialized
 *   successfully and can be used as a payment method.
 */
export const init = async (options) => {
  isIosApp = options.isIosApp;
  if (isIosApp) return true;

  try {
    if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
      log('DISABLED - cannot make payments', { ApplePaySession: !!window.ApplePaySession });
      return false;
    }
    if (!IS_ACTIVE_CARD_REQUIRED) return true;

    const canMakePayments = await window.ApplePaySession.canMakePaymentsWithActiveCard(MERCHANT_IDENTIFIER);
    log('canMakePaymentsWithActiveCard?', canMakePayments);
    return canMakePayments;
  } catch (err) {
    log('DISABLED - error', err);
    return false;
  }
};

/**
 * prepare
 *
 * Prepares the Braintree instance for use downstream during authorization
 *
 * @async
 * @returns {String} The id of the Apple Pay payment method
 * @throws {PaymentError} Any errors thrown by `getBraintreeInstance` are simply propagated here
 */
export const prepare = async () => {
  fandangoToken = getToken();
  braintreePaymentInstance = await getBraintreeInstance({ paymentMethodId: PAYMENT_METHOD_ID });
  return PAYMENT_METHOD_ID;
};

/**
 * select
 *
 * Saves ApplePay as the selected payment method and prepares the Braintree
 * instance for use downstream during authorization
 *
 * @async
 * @throws {PaymentError} Any errors thrown by `selectPaymentMethod` are simply propagated here
 */
export const select = async () => {
  const response = await selectPaymentMethod({
    paymentMethodId: PAYMENT_METHOD_ID,
    initializeBraintree: true,
  });
  fandangoToken = response.fandangoToken;
  braintreePaymentInstance = response.braintreeInstance;
};

/**
 * authorize
 *
 * Authorizes ApplePay as the payment method for the transaction and saves the Braintree nonce which is
 * used to complete the purchase.
 *
 * NOTE: If the user is within a native app the authorization will be handled *externally* so this simply notifies
 * the app to start that process and when it is complete (and it has the nonce) it will then call `completeNativeAuthorization`
 * to complete the process.
 *
 * @async
 * @throws {PaymentError}
 */
export const authorize = async ({ buyNow }) => {
  try {
    const paymentBreakdown = await getPaymentBreakdown(PAYMENT_METHOD_ID, buyNow);

    if (buyNow) {
      await selectWallet({ walletId: PAYMENT_METHOD_ID });
    }

    if (isIosApp) {
      if (!fandangoToken || !_.isArray(paymentBreakdown)) {
        onAuthorizationFailure();
        throw new PaymentError();
      }
      return new Promise((resolve, reject) => {
        // This promise will be fulfilled by `completeNativeAuthorization` which is triggered by the app
        resolveNativeAuthorization = resolve;
        rejectNativeAuthorization = reject;
        const authorizePaymentUrl = appCallbackUrls.getAuthorizeUrl(fandangoToken, paymentBreakdown);
        log('initializing native authorization', authorizePaymentUrl);
        window.open(authorizePaymentUrl, '_self');
      });
    }

    const {
      braintreeNonce,
      emailAddress,
    } = await getBraintreeNonce(paymentBreakdown);

    await updatePaymentInfo({
      braintreeNonce,
      emailAddress,
      postalCode,
      walletId: PAYMENT_METHOD_ID,
    });
    authorized = true;
  } catch (err) {
    onAuthorizationFailure();
    authorized = false;
    throw new PaymentError(err);
  }
};

/**
 * completeNativeAuthorization
 *
 * Completes the authorization process for the native payment method
 * This will resolve the promise returned by `authorize`
 *
 * NOTE: This function is exposed directly on the window so that the app can call it directly
 *
 * @async
 * @param {String} nonce - Braintree nonce
 * @param {Object=} contextData - Additional params that the app can pass during the authorization process
 * @param {Object=} contextData.additionalpaymentfields - User information for the app to pass from Apple Pay
 */
export const completeNativeAuthorization = async (nonce, contextData) => {
  log('complete native authorization');
  if (!resolveNativeAuthorization || !rejectNativeAuthorization) return;

  if (!nonce) {
    onAuthorizationFailure();
    authorized = false;
    return onAuthorizationFailure(new PaymentError('unknown nonce'));
  }

  try {
    await updatePaymentInfo({
      braintreeNonce: nonce,
      emailAddress: contextData?.additionalpaymentfields?.email,
      postalCode: contextData?.additionalpaymentfields?.billingZipCode || postalCode,
      walletId: PAYMENT_METHOD_ID,
    });
    authorized = true;
    window.open(appCallbackUrls.SUCCESS, '_self');
    resolveNativeAuthorization();
  } catch (err) {
    onAuthorizationFailure();
    authorized = false;
    rejectNativeAuthorization(err);
  }
};

export const isAuthorized = () => authorized;

export default PAYMENT_METHOD_ID;
